import React, { useEffect, useState, useContext } from "react";
import { LoginContext } from "../../context/login-context";
import { DragDropContext } from "@hello-pangea/dnd";

import useInvoices from "../../hooks/use-invoices.js";
import usePersistedState from "../../hooks/use-persisted-state.js";
import Filters, { getPresetDates } from "../../components/filters/filters.js";
import Column from "../../components/drag-n-drop/Column";
import HeroMetrics from "../../components/hero-metrics/hero-metrics.js";

import InvoiceModal from "./InvoiceModal.js";

import css from "./main.module.css";

const COLUMNS = {
	NEW:        { name: "New",        color: "#ededed", hoverColor: "#EAECF5" },
	REVIEW:     { name: "In Review",  color: "#ededed", hoverColor: "#EAECF5" },
	APPROVED:   { name: "Approved",   color: "#ededed", hoverColor: "#EAECF5" },
	PAID:       { name: "Paid",       color: "#ededed", hoverColor: "#EAECF5" },
	NO_PAYMENT: { name: "No Payment", color: "#ededed", hoverColor: "#EAECF5" },
};

const emptyColumns = () =>
	Object.fromEntries(
		Object.entries(COLUMNS).map(([key, col]) => [key, { ...col, items: [] }])
	);

const DEFAULT_PRESET = 'ytd';

const Kanban = (props) => {
	//todo add button that lets valley rep filter only on their accounts
	const [columns, setColumns] = useState(emptyColumns);

	const defaultDates = getPresetDates(DEFAULT_PRESET);
	const [preset, setPreset] = usePersistedState('kanbanPreset', DEFAULT_PRESET);
	const [startISO, setStartISO] = usePersistedState('kanbanStartDate', defaultDates.startDate.toISOString());
	const [endISO, setEndISO] = usePersistedState('kanbanEndDate', defaultDates.endDate.toISOString());

	useEffect(() => {
		if (preset && preset !== 'custom') {
			const { startDate: fresh, endDate: freshEnd } = getPresetDates(preset);
			if (fresh && freshEnd) {
				setStartISO(fresh.toISOString());
				setEndISO(freshEnd.toISOString());
			}
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const startDate = startISO ? new Date(startISO) : null;
	const endDate = endISO ? new Date(endISO) : null;

	const setStartDate = (date) => setStartISO(date ? date.toISOString() : null);
	const setEndDate = (date) => setEndISO(date ? date.toISOString() : null);

	const [selectedFarms, setSelectedFarms] = useState([]);

	const { runProtected, state: userData } = useContext(LoginContext);


	const {
		farms,
		strategicAccounts,
		flattenStratAccounts,
		filterAddon,
	} = props;

	const showEntity = strategicAccounts?.length > 1 || farms?.length > 1 //userData.user_role !== "FARM";


	const {
		invoices,
		selectedInvoice,
		showInvoice,
		setShowInvoice,
		setSelectedInvoice,
		refetchInvoices,
		handleSaveInvoiceItem,
		handleDeleteInvoiceItem,
		handleBulkDeleteInvoiceItems,
		handleUpdateInvoiceStatus,
		handleDeleteInvoice,
		handleUpdateInvoiceDetails,
		updateInvoiceDetailsComplete,
		updatingInvoiceDetails,
		error,
		isSuccess,
		isLoadingInvoices,
		isRefetchingInvoices,
		isDeleting,
		bulkItemDeleting,
		isSaving,
	} = useInvoices({ selectedFarms, startDate, endDate });

	useEffect(() => {
		if (startISO && endISO) {
			refetchInvoices();
		}
	}, [startISO, endISO, selectedFarms, refetchInvoices]);

	useEffect(() => {
		const updated = Object.fromEntries(
			Object.entries(COLUMNS).map(([key, col]) => [
				key,
				{ ...col, items: invoices?.filter((item) => item?.status === key) || [] },
			])
		);
		setColumns(updated);
	}, [invoices]);

	function cardOnClick(item) {
		setSelectedInvoice(item);
		setShowInvoice(true);
	}


	const onDragEnd = (result, columns, setColumns) => {
		if (!result.destination) return;
		const { source, destination } = result;

		const id = result.draggableId;
		const status = result.destination.droppableId;
		// console.log(result)

		if (source.droppableId !== destination.droppableId) {
			const sourceColumn = columns[source.droppableId];
			const destColumn = columns[destination.droppableId];
			const sourceItems = [...sourceColumn.items];
			const destItems = [...destColumn.items];
			const [removed] = sourceItems.splice(source.index, 1);
			destItems.splice(destination.index, 0, removed);
			setColumns({
				...columns,
				[source.droppableId]: {
					...sourceColumn,
					items: sourceItems,
				},
				[destination.droppableId]: {
					...destColumn,
					items: destItems,
				},
			});

			handleUpdateInvoiceStatus({ id, status });
		} else {
			const column = columns[source.droppableId];
			const copiedItems = [...column.items];
			const [removed] = copiedItems.splice(source.index, 1);
			copiedItems.splice(destination.index, 0, removed);
			setColumns({
				...columns,
				[source.droppableId]: {
					...column,
					items: copiedItems,
				},
			});
		}
	};

	return (
		<>
			<Filters
				farms={farms}
				strategicAccounts={strategicAccounts}
				startDate={startDate}
				setStartDate={setStartDate}
				setEndDate={setEndDate}
				endDate={endDate}
				selectedFarms={selectedFarms}
				setSelectedFarms={setSelectedFarms}
				multiFarmSelect={true}
				preset={preset}
				onPresetChange={setPreset}
			>
				{filterAddon}
			</Filters>

			<HeroMetrics
				invoiceLoading={isLoadingInvoices && !invoices}
				invoices={invoices}
			/>

			<div id="board" className={css.board}>
				<DragDropContext
					onDragEnd={(result) =>
						onDragEnd(result, columns, setColumns)
					}
				>
					{Object.entries(columns).map(
						([columnId, column], index) => (
							<div key={columnId} className={css.column}>
								<h2>{column.name}</h2>
								<Column
									dragDisabled={!userData?.isValmontUser}
									droppableId={columnId}
									key={columnId}
									index={index}
									column={column}
									onClick={cardOnClick}
								/>
							</div>
						)
					)}
				</DragDropContext>
			</div>
      
			<InvoiceModal
				strategicAccounts={strategicAccounts}
				flattenStratAccounts={flattenStratAccounts}
				open={showInvoice}
				setOpen={setShowInvoice}
				invoice={selectedInvoice}
				saveItem={handleSaveInvoiceItem}
				deleteItem={handleDeleteInvoiceItem}
				bulkDeleteItems={handleBulkDeleteInvoiceItems}
				isBulkDeleting={bulkItemDeleting}
				deleteInvoice={handleDeleteInvoice}
				error={error}
				isSuccess={isSuccess}
				isLoading={isLoadingInvoices || isRefetchingInvoices || isDeleting || isSaving}
				refetch={refetchInvoices}
				handleUpdateInvoiceDetails={handleUpdateInvoiceDetails}
				updateInvoiceDetails={handleUpdateInvoiceDetails}
				updateInvoiceDetailsComplete={updateInvoiceDetailsComplete}
				updatingInvoiceDetails={updatingInvoiceDetails}
			/>
		</>
	);
};

export default Kanban;
